import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { downloadReport, ReportFormat } from './utils/protocolReport';
import './SummaryView.css';

// v1.5.0: Added markdown rendering support for AI-generated content
// v1.7.0: Strip markdown when copying to clipboard for clean paste into InfoEd
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Strip common markdown formatting for clipboard copy
const stripMarkdown = (text: string): string => {
  return text
    // Remove headers (# ## ### etc.)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold/italic markers
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // Remove inline code backticks
    .replace(/`([^`]+)`/g, '$1')
    // Remove horizontal rules
    .replace(/^---+$/gm, '')
    .replace(/^\*\*\*+$/gm, '')
    // Clean up extra whitespace from removed elements
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

interface Question {
  number: string;
  text: string;
  type: string;
  answer: string;
  answered: boolean;
}

interface Section {
  number: number;
  name: string;
  questions: Question[];
}

interface SummaryData {
  protocolId: number;
  sections: Section[];
}

interface SummaryViewProps {
  protocolId: number;
  protocolTitle: string;
  authHeaders: Record<string, string>;
}

const SummaryView: React.FC<SummaryViewProps> = ({ protocolId, protocolTitle, authHeaders }) => {
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  useEffect(() => {
    fetchSummary();
  }, [protocolId]);

  const fetchSummary = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching summary for protocol:', protocolId);
      console.log('🔍 API URL:', `${API_BASE_URL}/api/protocols/${protocolId}/summary`);
      console.log('🔍 Auth headers:', authHeaders);
      
      const response = await fetch(`${API_BASE_URL}/api/protocols/${protocolId}/summary`, {
        headers: authHeaders
      });
      
      console.log('🔍 Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Server error:', errorData);
        throw new Error(errorData.error || `Server returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Summary data received:', data);
      setSummaryData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('❌ Error fetching summary:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopiedItem(itemId);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };

  const copyQuestion = (question: Question) => {
    // Strip markdown for clean paste into InfoEd
    const text = stripMarkdown(question.answer);
    copyToClipboard(text, `q-${question.number}`);
  };

  const copySection = (section: Section) => {
    let text = '';

    section.questions.forEach((q, index) => {
      // Strip markdown from each answer
      text += stripMarkdown(q.answer);
      // Add separator between questions, but not after the last one
      if (index < section.questions.length - 1) {
        text += '\n\n---\n\n';
      }
    });

    copyToClipboard(text, `s-${section.number}`);
  };

  const handleDownload = (format: ReportFormat) => {
    if (!summaryData) return;
    try {
      downloadReport(summaryData, protocolTitle, format);
    } catch (err) {
      console.error('Failed to generate report:', err);
      alert('Failed to generate the report. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="summary-view">
        <div className="summary-header">
          <h1>Summary</h1>
        </div>
        <div className="summary-loading">Loading summary...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="summary-view">
        <div className="summary-header">
          <h1>Summary</h1>
        </div>
        <div className="summary-error">
          <p>Error: {error}</p>
          <button onClick={fetchSummary}>Retry</button>
        </div>
      </div>
    );
  }

  if (!summaryData || summaryData.sections.length === 0) {
    return (
      <div className="summary-view">
        <div className="summary-header">
          <h1>Summary</h1>
        </div>
        <div className="summary-empty">
          <p>No answers found for this protocol.</p>
          <p>Complete some questions first, then return to view the summary.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="summary-view">
      <div className="summary-header">
        <div className="summary-title-section">
          <h1>Summary</h1>
          <h2>{protocolTitle}</h2>
        </div>
        <div className="summary-header-actions">
          <button
            className="download-report-button"
            onClick={() => handleDownload('word')}
            title="Download a Word document of your completed sections to edit and copy into InfoEd"
          >
            ⬇ Download Word
          </button>
          <button
            className="download-report-button"
            onClick={() => handleDownload('markdown')}
            title="Download a Markdown file of your completed sections"
          >
            ⬇ Download Markdown
          </button>
        </div>
      </div>

      <div className="summary-instructions">
        <p><strong>Instructions:</strong> Copy individual questions or whole sections directly into the InfoEd IACUC form,
        or download the full draft as Word or Markdown to edit and copy from later.</p>
      </div>

      <div className="summary-content">
        {summaryData.sections.map(section => (
          <div key={section.number} className="summary-section">
            <div className="section-header">
              <h2>Section {section.number}: {section.name}</h2>
              <button 
                className="copy-section-button"
                onClick={() => copySection(section)}
              >
                {copiedItem === `s-${section.number}` ? '✓ Copied!' : '📋 Copy Section'}
              </button>
            </div>

            <div className="section-questions">
              {section.questions.map(question => (
                <div 
                  key={question.number} 
                  className={`summary-question ${!question.answered ? 'not-answered' : ''}`}
                >
                  <div className="question-header">
                    <div className="question-info">
                      <h3>Question {question.number}</h3>
                      <p className="question-text">{question.text}</p>
                    </div>
                    <button 
                      className="copy-question-button"
                      onClick={() => copyQuestion(question)}
                      disabled={!question.answered}
                    >
                      {copiedItem === `q-${question.number}` ? '✓ Copied!' : '📄 Copy'}
                    </button>
                  </div>
                  
                  <div className="question-answer">
                    <ReactMarkdown>{question.answer}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="summary-footer">
        <button
          className="download-report-button"
          onClick={() => handleDownload('word')}
          title="Download a Word document of your completed sections"
        >
          ⬇ Download Word
        </button>
        <button
          className="download-report-button"
          onClick={() => handleDownload('markdown')}
          title="Download a Markdown file of your completed sections"
        >
          ⬇ Download Markdown
        </button>
      </div>
    </div>
  );
};

export default SummaryView;
